import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  Select, 
  Button, 
  DatePicker, 
  InputNumber, 
  Space, 
  Table,
  Typography,
  Divider,
  notification,
  Spin,
  Avatar,
  Grid
} from 'antd';
import ColumnsCardList from '../common/ColumnsCardList';
import { PlusOutlined, DeleteOutlined, PhoneOutlined, UserOutlined } from '@ant-design/icons';
import { clientsApi, ordersApi, productsApi, whatsappApi } from '../../utils/api'; // Importation des API
import WhatsAppProviderBadge from '../whatsapp/WhatsAppProviderBadge';
import ProductSelectionModal from './ProductSelectionModal';
import { supabase } from '../../utils/supabase';
// Importation de react-phone-number-input pour la gestion et validation des numéros de téléphone (CI par défaut)
import 'react-phone-number-input/style.css';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import { getDeviseActive } from '../../utils/devise';

const { Option } = Select;
const { TextArea } = Input;
const { Title } = Typography;

const NewOrderModal = ({ visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  // Modal.useModal : la confirmation reste dans l'arbre React (accès aux providers, ex. QueryClient),
  // contrairement à Modal.confirm statique qui provoquait « No QueryClient set »
  const [modal, confirmContextHolder] = Modal.useModal();
  // Mobile (< md) : modale plein écran, champs empilés, produits en cartes
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [searchingCustomer, setSearchingCustomer] = useState(false);
  const [isProductModalVisible, setProductModalVisible] = useState(false);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setSelectedItems([]);
      setCustomers([]);
    }
  }, [visible, form]);

  const searchCustomers = async (value) => {
    if (value.length < 3) return;
    
    setSearchingCustomer(true);
    try {
      // Appel API pour rechercher des clients par nom ou téléphone
      const response = await clientsApi.getAll({ search: value });
      setCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Erreur lors de la recherche de clients:', error);
    } finally {
      setSearchingCustomer(false);
    }
  };

  const openProductSelectionModal = () => {
    setProductModalVisible(true);
  };

  const handleProductSelect = (selectedItemData) => {
    console.log('selectedItemData', selectedItemData);
    const productId = selectedItemData.productId;
    const variantId = selectedItemData.type === 'variant' ? selectedItemData.id : null;
    const stock = selectedItemData.stock;

    // Vérifier si le produit est en stock
    if (stock <= 0) {
      notification.error({
        message: 'Stock épuisé',
        description: 'Ce produit est en rupture de stock.'
      });
      return;
    }

    const existingItemIndex = selectedItems.findIndex(
      item => item.product_id === productId && item.variant_id === variantId
    );

    let updatedItems;

    if (existingItemIndex > -1) {
      // Vérifier si l'ajout dépasse le stock
      if (selectedItems[existingItemIndex].quantity + 1 > stock) {
        notification.warning({
          message: 'Stock insuffisant',
          description: `La quantité demandée dépasse le stock disponible (${stock}).`
        });
        return;
      }

      updatedItems = selectedItems.map((item, index) => {
        if (index === existingItemIndex) {
          const newQuantity = item.quantity + 1;
          return {
            ...item,
            quantity: newQuantity,
            total: item.price * newQuantity,
            stock: stock 
          };
        }
        return item;
      });
      setSelectedItems(updatedItems);

    } else {
      const newItem = {
        key: Date.now(),
        product_id: productId,
        variant_id: variantId,
        item_name: selectedItemData.name,
        image: selectedItemData.image, // Ajout de l'image
        price: selectedItemData.price,
        sku: selectedItemData.sku,
        quantity: 1,
        total: selectedItemData.price * 1,
        stock: stock
      };
      updatedItems = [...selectedItems, newItem];
      setSelectedItems(updatedItems);
    }

    setProductModalVisible(false);
    calculateTotal(updatedItems);
  };

  const removeItem = (key) => {
    const newItems = selectedItems.filter(item => item.key !== key);
    setSelectedItems(newItems);
    calculateTotal(newItems);
  };

  const handleQuantityChange = (value, key) => {
    const updatedItems = selectedItems.map(item => {
      if (item.key === key) {
        let newQuantity = value;
        
        // Vérifier la limite de stock
        if (item.stock !== undefined && item.stock !== null && value > item.stock) {
           notification.warning({
            message: 'Stock insuffisant',
            description: `La quantité demandée dépasse le stock disponible (${item.stock}).`
          });
          newQuantity = item.stock;
        }

        const newItem = {
          ...item,
          quantity: newQuantity,
          total: item.price * newQuantity
        };
        return newItem;
      }
      return item;
    });

    setSelectedItems(updatedItems);
    calculateTotal(updatedItems);
  };

  const calculateTotal = (itemsToCalculate = selectedItems) => {
    const subtotal = itemsToCalculate.reduce((sum, item) => sum + (item.total || 0), 0);
    
    const shippingFee = form.getFieldValue('shipping_fee') || 0;
    const discountAmount = form.getFieldValue('discount_amount') || 0;
    
    const totalAmount = subtotal + shippingFee - discountAmount;
    
    form.setFieldsValue({ total_amount: totalAmount.toFixed(2) });
  };

  const handleCustomerSelect = (value) => {
    const customer = customers.find(c => c.id === value);
    if (customer) {
      let deliveryAddress = '';
      console.log(customer);
      if (customer.customer_addresses && customer.customer_addresses.length > 0) {
        const defaultAddr = customer.customer_addresses.find(addr => addr.is_default === true);
        const addressToUse = defaultAddr || customer.customer_addresses[0];
        
        deliveryAddress = `${addressToUse.address_line1 || ''}${addressToUse.city ? ', ' + addressToUse.city : ''}`;
      }else if(customer.default_address){
        deliveryAddress = customer.default_address;
      }

      form.setFieldsValue({
        customer_name: customer.name,
        customer_phone: customer.phone,
        delivery_address: deliveryAddress 
      });
    }
  };

  /**
   * Vérifie si la préparatrice est activée puis propose l'envoi manuel du lien
   * public de la commande venant d'être créée pour la boutique active. La création
   * reste réussie même si l'utilisateur refuse l'envoi ou si WhatsApp rencontre une erreur.
   * @param {object} order - Commande créée, utilisée pour construire son lien public.
   */
  const offerPreparationMessage = async (order) => {
    try {
      if (!order?.id) return;

      // Les réglages de la préparatrice sont isolés par boutique dans storefront_settings.
      const shopId = window.localStorage.getItem('activeShopId');
      if (!shopId) return;

      const { data: preparationSettings, error } = await supabase
        .from('shops')
        .select('storefront_settings')
        .eq('id', shopId)
        .single();

      if (error) throw error;

      // La configuration complète est stockée dans le JSON propre à la boutique active.
      const shopSettings = preparationSettings?.storefront_settings || {};
      if (!shopSettings.daily_preparation_summary_enabled || !shopSettings.daily_preparation_summary_phone) {
        return;
      }

      const orderLink = `${window.location.origin}/order-view/${order.id}`;
      const preparationMessage = `Bonjour,\n\nUne nouvelle commande vient d’être créée.\n\nConsultez-la ici :\n${orderLink}`;

      modal.confirm({
        title: 'Envoyer à la préparatrice ?',
        content: (
          <div>
            <p>Voulez-vous envoyer maintenant le lien de cette nouvelle commande à la préparatrice ?</p>
            <WhatsAppProviderBadge />
          </div>
        ),
        okText: 'Envoyer le lien',
        cancelText: 'Plus tard',
        onOk: async () => {
          try {
            // L'intercepteur API transmet X-Shop-Id afin que l'envoi utilise la connexion WhatsApp de cette boutique.
            await whatsappApi.sendMessage({
              phone: shopSettings.daily_preparation_summary_phone,
              message: preparationMessage
            });
            notification.success({
              message: 'Lien envoyé',
              description: 'Le lien de la nouvelle commande a été envoyé à la préparatrice.'
            });
          } catch (sendError) {
            console.error('Erreur envoi lien préparatrice :', sendError);
            notification.error({
              message: 'Envoi impossible',
              description: 'La commande est créée, mais le lien n’a pas pu être envoyé à la préparatrice.'
            });
          }
        }
      });
    } catch (settingsError) {
      console.error('Erreur vérification réglages préparatrice :', settingsError);
    }
  };

  /**
   * Crée la commande à partir du formulaire et déclenche ensuite, si configuré,
   * la demande d'envoi de son lien public à la préparatrice.
   */
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (selectedItems.length === 0) {
        notification.error({
          message: 'Erreur',
          description: 'Veuillez ajouter au moins un produit à la commande.',
        });
        return;
      }

      setLoading(true);
      console.log(values);
      console.log(selectedItems);

      // Formater la date de livraison en YYYY-MM-DD pour éviter les problèmes de fuseau horaire
      // On extrait uniquement la date sans l'heure pour que tous les utilisateurs voient la même date
      const orderData = {
        ...values,
        delivery_date: values.delivery_date ? values.delivery_date.format('YYYY-MM-DD') : null,
        order_items: selectedItems.map(item => ({
          product_id: item.product_id,
          variant_id: item.variant_id,
          quantity: item.quantity,
          item_name: item.item_name,
          price: item.price,
          total: item.total
        })),
        status: 'pending',
        payment_status: 'pending',
      };
      console.log(orderData);
  
      
      const response = await ordersApi.create(orderData);
      
      if (response && response.data) {
        notification.success({
          message: 'Succès',
          description: `Commande ${response.data.order?.order_number || response.data.order?.id || 'N/A'} créée avec succès.`,
        });
        await offerPreparationMessage(response.data.order);
        onSuccess();
      } else {
        throw new Error('Erreur lors de la création de la commande');
      }
    } catch (error) {
      console.error('Erreur lors de la création de la commande:', error);
      notification.error({
        message: 'Erreur',
        description: 'Impossible de créer la commande. Veuillez réessayer.'
      });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Produit / Variant',
      dataIndex: 'item_name',
      key: 'item_name',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar 
            shape="square" 
            size={50} 
            src={record.image} 
            style={{ marginRight: 12, width: 40, minWidth: 40, height: 40 }} 
            icon={<UserOutlined />} // Fallback icon si pas d'image
          />
          <div>
            <div style={{ fontWeight: 500 }}>{text || 'Produit non sélectionné'}</div>
            {record.sku && <div style={{ fontSize: '0.8em', color: 'gray' }}>SKU: {record.sku}</div>}
          </div>
        </div>
      ),
    },
    {
      title: 'Quantité',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 120,
      render: (_, record) => (
        <InputNumber
          min={1}
          max={record.stock}
          style={{ width: '100%' }}
          value={record.quantity}
          onChange={(value) => handleQuantityChange(value, record.key)}
        />
      ),
    },
    {
      title: 'Prix unitaire',
      dataIndex: 'price',
      key: 'price',
      width: 120,
      render: (text) => text ? `${text.toFixed(2)} ${getDeviseActive()}` : `0.00 ${getDeviseActive()}`,
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 120,
      render: (text) => text ? `${text.toFixed(2)} ${getDeviseActive()}` : `0.00 ${getDeviseActive()}`,
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeItem(record.key)}
        />
      ),
    },
  ];

  return (
    <>
    {confirmContextHolder}
    <Modal
      title="Nouvelle commande"
      open={visible}
      onCancel={onCancel}
      width={isMobile ? '95vw' : 800}
      style={isMobile ? { top: 20 } : undefined}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Annuler
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          Créer la commande
        </Button>,
      ]}
    >
      <Spin spinning={loading} tip="Création de la commande...">
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            delivery_date: null,
            payment_method: 'cash',
            status: 'pending',
            payment_status: 'pending',
            total_amount: 0,
            shipping_fee: 0,
            discount_amount: 0 
          }}
        >
          <Title level={5}>Informations client</Title>
          <div className="customer-search">
            <Form.Item
              name="customer_id"
              label="Rechercher un client"
            >
              <Select
                showSearch
                placeholder="Rechercher par nom ou téléphone"
                optionFilterProp="children"
                loading={searchingCustomer}
                onChange={handleCustomerSelect}
                onSearch={searchCustomers}
                filterOption={false}
                allowClear
                optionLabelProp="label"
              >
                {customers.map(customer => (
                  <Option 
                    key={customer.id} 
                    value={customer.id}
                    label={`${customer.name} (${customer.phone})`}
                  >
                    <div>
                      <UserOutlined /> {customer.name}
                    </div>
                    <div>
                      <PhoneOutlined /> {customer.phone}
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div className="customer-info" style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 0 : '16px' }}>
            <Form.Item
              name="customer_name"
              label="Nom du client"
              style={{ flex: 1 }}
              rules={[{ required: true, message: 'Veuillez saisir le nom du client' }]}
            >
              <Input placeholder="Nom du client" />
            </Form.Item>

            {/* Champ téléphone avec validation et code pays par défaut CI (+225) */}
            <Form.Item
              name="customer_phone"
              label="Téléphone"
              style={{ flex: 1 }}
              rules={[
                { required: true, message: 'Veuillez saisir le téléphone du client' },
                {
                  validator: (_, value) => {
                    if (value && !isValidPhoneNumber(value)) {
                      return Promise.reject(new Error('Numéro de téléphone invalide'));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <PhoneInput
                defaultCountry="CI"
                placeholder="Numéro de téléphone"
                international
                countryCallingCodeEditable={false}
                className="ant-input" // Pour appliquer le style de base d'Ant Design si nécessaire, ou ajuster le CSS
                style={{ padding: 0, border: 'none' }} // Ajustement du style pour l'intégration visuelle
              />
            </Form.Item>
          </div>

          <Form.Item
            name="delivery_address"
            label="Adresse de livraison"
            rules={[{ required: true, message: 'Veuillez saisir l\'adresse de livraison' }]}
          >
            <TextArea placeholder="Adresse de livraison" rows={2} />
          </Form.Item>

          <Divider />

          <Title level={5}>Détails de la commande</Title>

          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 0 : '16px' }}>
            <Form.Item
              name="delivery_date"
              label="Date de livraison"
              style={{ flex: 1 }}
              rules={[{ required: true, message: 'Veuillez sélectionner une date de livraison' }]}
            >
              <DatePicker style={{ width: '100%' }} placeholder="Date de livraison" />
            </Form.Item>

            <Form.Item
              name="payment_method"
              label="Méthode de paiement"
              style={{ flex: 1 }}
              rules={[{ required: true, message: 'Veuillez sélectionner une méthode de paiement' }]}
            >
              <Select placeholder="Sélectionner une méthode de paiement">
                <Option value="cash">Espèces</Option>
                <Option value="card">Carte bancaire</Option>
                <Option value="transfer">Virement bancaire</Option>
                <Option value="mobile_money">Mobile Money</Option>
              </Select>
            </Form.Item>
          </div>

          <Divider />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <Title level={5} style={{ margin: 0 }}>Produits</Title>
            <Button type="primary" icon={<PlusOutlined />} onClick={openProductSelectionModal}>
              Ajouter un produit
            </Button>
          </div>

          {isMobile ? (
            <ColumnsCardList
              columns={columns}
              dataSource={selectedItems}
              rowKey="key"
              headerKeys={['item_name']}
              actionKeys={['action']}
              emptyText="Aucun produit ajouté"
            />
          ) : (
            <Table
              columns={columns}
              dataSource={selectedItems}
              pagination={false}
              size="small"
              locale={{ emptyText: "Aucun produit ajouté" }}
            />
          )}

          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'flex-end', gap: isMobile ? '12px' : '16px', marginTop: '16px' }}>
            <Form.Item
              name="shipping_fee"
              label="Livraison"
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                style={{ width: isMobile ? '100%' : '120px' }}
                min={0}
                step={500}
                formatter={(value) => `${value} ${getDeviseActive()}`}
                parser={(value) => value.replace(` ${getDeviseActive()}`, '')}
                onChange={() => calculateTotal()} 
              />
            </Form.Item>
            <Form.Item
              name="discount_amount"
              label="Réduction"
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                style={{ width: isMobile ? '100%' : '120px' }}
                min={0}
                step={500}
                formatter={(value) => `${value} ${getDeviseActive()}`}
                parser={(value) => value.replace(` ${getDeviseActive()}`, '')}
                onChange={() => calculateTotal()}
              />
            </Form.Item>
          </div>

          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'flex-end', marginTop: '16px' }}>
            <Form.Item
              name="total_amount"
              label="Total"
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                style={{ width: isMobile ? '100%' : '150px' }}
                readOnly
                formatter={(value) => `${value} ${getDeviseActive()}`}
                parser={(value) => value.replace(` ${getDeviseActive()}`, '')}
              />
            </Form.Item>
          </div>

          <Divider />

          <Form.Item
            name="notes"
            label="Notes"
          >
            <TextArea placeholder="Notes additionnelles sur la commande" rows={3} />
          </Form.Item>
        </Form>
      </Spin>

      <ProductSelectionModal
        visible={isProductModalVisible}
        onCancel={() => setProductModalVisible(false)}
        onSelect={handleProductSelect} 
      />

    </Modal>
    </>
  );
};

export default NewOrderModal;
